// ============================================================================
// analytics.js — the conversion funnel (V2 plan §8, Phase D1)
// ============================================================================
// "Conversion instrumentation from day one (designs started, logo uploaded,
// completed, 3D opened, shared, quote submitted)" was in the D1 spec and never
// got built. The tool went live, and started taking real quote submissions,
// with no way to answer where people stop.
//
// SIX EVENTS, ONCE EACH PER SESSION. A funnel counts PEOPLE, not actions: a
// buyer who re-uploads a logo three times is one person who uploaded a logo, and
// counting the retries would quietly inflate the widest step and flatter the
// drop-off. `fired` is what keeps each step honest.
//
// FIRE-AND-FORGET, ALWAYS. Every call is wrapped so that no failure — offline,
// blocked by an extension, CSP, a 429 — can surface to a buyer mid-design or
// break the flow that produced the event. An analytics call that can throw is a
// bug generator; this one returns undefined and swallows everything.
//
// `sendBeacon` where available: it survives the page being navigated away from,
// which matters for the last event in the funnel (the quote submit is followed
// by a modal and often a close).
// ============================================================================

import { isRepMode } from './rep-mode.js';

const ENDPOINT = 'https://rfq-worker.ryan-29b.workers.dev/wd-event';

/** The funnel, in order. Mirrors WD_EVENTS in the worker, which REJECTS anything else — an
 *  open-ended key space is how a counters blob quietly becomes a schema-less event log. */
export const WD_EVENTS = Object.freeze({
  STARTED: 'design_started',
  LOGO: 'logo_uploaded',
  BUILT: 'design_built',
  REVEAL_3D: 'reveal_3d',
  SHARED: 'design_shared',
  QUOTED: 'quote_submitted',
});

const fired = new Set();

/**
 * Count one funnel step. Idempotent per page session, silent on every failure.
 * @param {string} event one of WD_EVENTS
 */
export function wdTrack(event) {
  try {
    if (!event || fired.has(event)) return;
    // REP MODE COUNTS NOTHING. The portal edition runs the same code on the same six
    // counters, so a rep demoing a plate would land in the buyers' funnel — inflating
    // its top step, which is the denominator every rate below it is measured against.
    // The one number this file exists to keep honest is the one internal use would break.
    if (isRepMode()) return;
    fired.add(event);
    const body = JSON.stringify({ event });
    // Opportunistic GA4: the storefront MAY have a gtag from Shopify's own pixel injection, and
    // where it does, Ryan gets these in a UI he already uses at no cost. Nothing depends on it —
    // the theme's Liquid defines no gtag, and the standalone pages.dev build certainly has none.
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', event, { event_category: 'weight_designer' });
    }
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      // Beacon can't set Content-Type on a string, so send a Blob that carries it — the worker
      // parses JSON and a bare text/plain body would 400.
      navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'application/json' }));
      return;
    }
    fetch(ENDPOINT, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true })
      .catch(() => {});
  } catch {
    /* instrumentation must never break the thing it instruments */
  }
}

// ── CAMPAIGN ATTRIBUTION ─────────────────────────────────────────────────────────────────────
// The lead pipeline has always accepted a `utm` object, and `/wd-quote` has always forwarded one —
// but nothing in the designer ever captured it, so every weight lead reached Marketing Ops with no
// campaign against it. That is not a small omission on a tool whose whole point is to be pointed at
// from ads: an unattributed lead cannot be credited to the spend that produced it.
//
// ⚠ READ AT BOOT AND REMEMBERED, because a design session is long and multi-step. The buyer lands
// on `/pages/weight-designer?utm_source=…`, then spends twenty minutes designing; by the time they
// submit, the URL may have been rewritten by a share link (`?d=`) or a preview flag. Reading the
// query string at SUBMIT time would therefore attribute a large share of real campaign traffic to
// nothing. sessionStorage, not localStorage: the campaign belongs to this visit.
const UTM_KEY = 'wd:v2:utm';
// ⛔ THE GOOGLE CAMPAIGN ID RIDES IN `gad_campaignid`, NOT `utm_campaign` (Ryan 2026-10-06). An auto-tagged
// Google ad lands with `gclid`, `gad_source` and `gad_campaignid` and usually no utm_* at all, so keeping only
// `gclid` told Marketing Ops the lead was Google and never which campaign: Mojoe's Gym and four others sat on
// no campaign row. rfq-worker reads `gad_campaignid` as the campaign. `gbraid` / `wbraid` are the iOS click IDs.
const UTM_FIELDS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'gad_source', 'gad_campaignid', 'gbraid', 'wbraid', 'fbclid', 'msclkid'];

/** Capture from the current URL (once per session, first touch wins) and return what we hold. */
export function captureUtm() {
  try {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search || '');
    const found = {};
    for (const f of UTM_FIELDS) {
      const v = params.get(f);
      if (v) found[f] = String(v).slice(0, 200);
    }
    // FIRST TOUCH WINS. A buyer who arrives from an ad and later opens a share link of their own
    // design must not have the campaign overwritten by the second visit's empty query.
    const held = capturedUtm();
    if (!Object.keys(found).length) return held;
    if (held && Object.keys(held).length) return held;
    sessionStorage.setItem(UTM_KEY, JSON.stringify(found));
    return found;
  } catch {
    return null;
  }
}

/** What we captured earlier this session, or null. */
export function capturedUtm() {
  try {
    const raw = sessionStorage.getItem(UTM_KEY);
    if (!raw) return null;
    const o = JSON.parse(raw);
    return o && typeof o === 'object' && Object.keys(o).length ? o : null;
  } catch {
    return null;
  }
}
